import { AuthenticateChannel } from '../thirdparty-auth/core/types';
import { base64UrlEncode } from './pkce';
import { generateRandomString } from './utils';

export const OAUTH_STATE_RANDOM_LENGTH = 15;

export const generateAesGcmKey = async (
  _options: Record<string, never> = {}
): Promise<CryptoKey> => {
  return await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
};

export const exportKeyToJwk = async ({
  key,
}: {
  key: CryptoKey;
}): Promise<JsonWebKey> => {
  return (await crypto.subtle.exportKey('jwk', key)) as JsonWebKey;
};

export const importJwkToKey = async ({
  jwk,
}: {
  jwk: JsonWebKey;
}): Promise<CryptoKey> => {
  return await crypto.subtle.importKey(
    'jwk',
    jwk,
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
};

const base64UrlDecodeToUint8Array = ({
  input,
}: {
  input: string;
}): Uint8Array => {
  // convert base64url to base64
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const pad =
    base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));
  const normalized = base64 + pad;
  const binary = atob(normalized);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

export const createEncryptedState = async ({
  type,
}: {
  type: AuthenticateChannel;
}): Promise<{
  // compact state string to be used in URL (<=100 chars)
  state: string;
  // JSON Web Key for AES-GCM, to be stored in sessionStorage
  keyJwk: JsonWebKey;
  // original inner random for optional comparison/debugging
  innerState: string;
}> => {
  const innerState = generateRandomString({
    length: OAUTH_STATE_RANDOM_LENGTH,
  });
  const plaintext = `type=${type}&state=${innerState}`;

  const key = await generateAesGcmKey({});
  const keyJwk = await exportKeyToJwk({ key });

  const encoder = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV
  const ciphertextBuffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoder.encode(plaintext)
  );
  const ciphertext = new Uint8Array(ciphertextBuffer);

  // Pack IV || ciphertext and base64url encode into single string
  const combined = new Uint8Array(iv.length + ciphertext.length);
  combined.set(iv, 0);
  combined.set(ciphertext, iv.length);
  const state = base64UrlEncode({ buffer: combined });

  return { state, keyJwk, innerState };
};

export const decryptState = async ({
  state,
  keyJwk,
}: {
  state: string;
  keyJwk: JsonWebKey;
}): Promise<{ type?: AuthenticateChannel; state?: string } | null> => {
  try {
    const combined = base64UrlDecodeToUint8Array({ input: state });
    const iv = combined.slice(0, 12);
    const ciphertext = combined.slice(12);
    const key = await importJwkToKey({ jwk: keyJwk });
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      ciphertext
    );
    const text = new TextDecoder().decode(decrypted);
    // parse "type=xxx&state=yyy"
    const parts = text.split('&').reduce((acc, kv) => {
      const idx = kv.indexOf('=');
      const k = idx >= 0 ? kv.slice(0, idx) : kv;
      const v = idx >= 0 ? kv.slice(idx + 1) : '';
      if (k) acc[k] = decodeURIComponent(v);
      return acc;
    }, {} as Record<string, string | undefined>);
    return {
      type: (parts.type as unknown) as AuthenticateChannel,
      state: parts.state,
    };
  } catch {
    return null;
  }
};
